import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Checkbox } from 'designSystem/components/Checkbox'
import { Input } from 'designSystem/components/Input'
import {
  expectedBandTraces,
  residualBandTraces,
} from 'features/sentinels/components/chartBands'
import { useOpenSentinelCase } from 'features/sentinels/hooks/useOpenSentinelCase'
import { useScoreRecent } from 'features/sentinels/hooks/useScoreRecent'
import {
  ErrorText,
  NativeSelect,
  SizedPlot,
  Subtitle,
} from 'features/sentinels/styles'
import { MAX_SCORE_DAYS } from 'features/sentinels/types'
import { Layout, PlotData, PlotMouseEvent } from 'plotly.js'
import React, { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const FG = CantileverTheme.colors.grayscale['100']
const GRID = CantileverTheme.colors.grayscale['600']
const METHOD_COLORS = [
  '#22d3ee',
  '#f97316',
  '#ef4444',
  CantileverTheme.colors.green['400'],
]

const methodColor = (mode: 'target' | 'multitag', index: number) =>
  mode === 'target'
    ? METHOD_COLORS[index % METHOD_COLORS.length]
    : index === 0
      ? CantileverTheme.colors.blue['400']
      : CantileverTheme.colors.green['400']

const SubTitle = styled.h3`
  font-size: 0.84375rem;
  margin: 0.875rem 0 0.375rem;
  color: ${({ theme }) => theme.colors.grayscale['200']};
`

const RunRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DateInput = styled(Input)`
  width: 10rem;
`

const RangeHint = styled.span`
  color: ${({ theme }) => theme.colors.grayscale['400']};
`

const ModelFilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem 1.125rem;
  margin: 0.5rem 0 0.75rem;
  align-items: center;
`

const ModelFilterLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grayscale['200']};
  user-select: none;
`

const ColorDot = styled.span<{ $color: string }>`
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 9999px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const short = (t: string) => t.split('-').slice(1).join('-') || t

const isoDay = (offsetDays = 0) => {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  return d.toISOString().slice(0, 10)
}

/** Plotly date axes need ISO bounds; date-only end → end of UTC day. */
const axisBound = (value: string, endOfDay = false) => {
  if (value.includes('T')) return value
  /* eslint-disable lingui/no-unlocalized-strings -- ISO timestamp syntax, not user-facing copy. */
  return endOfDay ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`
  /* eslint-enable lingui/no-unlocalized-strings */
}

const dateAxis = (
  windowStart?: string,
  windowEnd?: string
): Partial<Layout>['xaxis'] => {
  const start = windowStart?.trim()
  const end = windowEnd?.trim()
  const dayStart = start?.slice(0, 10)
  const dayEnd = end?.slice(0, 10)
  return {
    type: 'date',
    // eslint-disable-next-line lingui/no-unlocalized-strings -- Plotly date format is deferred chart syntax.
    tickformat: '%Y-%m-%d',
    nticks: 8,
    ...(start && end
      ? {
          range: [axisBound(start), axisBound(end, true)] as [string, string],
          title: {
            // eslint-disable-next-line lingui/no-unlocalized-strings -- Plotly axis labels are deferred in this extraction wave.
            text: `x: ${dayStart} → ${dayEnd}`,
            font: { color: FG, size: 10 },
          },
        }
      : {}),
    tickfont: { color: FG, size: 10 },
    gridcolor: GRID,
    automargin: true,
  }
}
/**
 * A Plotly date-axis click returns x as a number (epoch) or a
 * `'YYYY-MM-DD HH:MM:SS[.sss]'` string with no timezone (plotted as UTC). Send
 * it back as an explicit-UTC ISO string; the service nearest-matches it to a
 * scored bin.
 */
const tsFromPointX = (
  x: PlotMouseEvent['points'][number]['x']
): string | null => {
  if (x === undefined || x === null) return null
  if (typeof x === 'number') return new Date(x).toISOString()
  const s = String(x)
  const iso = s.includes('T') ? s : s.replace(' ', 'T')
  return iso.endsWith('Z') || /[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`
}

export interface RecentScorePanelProps {
  entryId: string
  site: string
  famOf: Record<string, string>
  mode?: 'target' | 'multitag'
  target?: string
  methodLabels: Record<string, string>
  /** Called after a case is opened from a clicked breach point. */
  onCaseOpened?: () => void
}

export const RecentScorePanel: FC<RecentScorePanelProps> = ({
  entryId,
  site,
  famOf,
  mode = 'multitag',
  target,
  methodLabels,
  onCaseOpened,
}) => {
  const { t } = useLingui()

  const { state, run } = useScoreRecent()
  const { state: caseState, open: openCase } = useOpenSentinelCase()

  /**
   * Open a case at the clicked point. Only meaningful for target-mode sentinels
   * (they have a band); the service rejects in-band clicks, so we let it be the
   * authority on "out of band" rather than re-deriving it here.
   */
  const onPointClick = useCallback(
    (event: Readonly<PlotMouseEvent>) => {
      if (mode !== 'target') return
      const ts = tsFromPointX(event.points?.[0]?.x)
      if (!ts) return
      void openCase(entryId, site, ts).then((ok) => {
        if (ok) onCaseOpened?.()
      })
    },
    [mode, entryId, site, openCase, onCaseOpened]
  )
  const [selTag, setSelTag] = useState<string | null>(null)
  // Default to the last week so the picker matches a meaningful live window.
  const [startDate, setStartDate] = useState<string>(isoDay(-7))
  const [endDate, setEndDate] = useState<string>(isoDay())
  // Default all models selected; only explicit false hides a model.
  const [selected, setSelected] = useState<Record<string, boolean>>({})

  const d = state.status === 'done' ? state.data : undefined
  const tags = useMemo(() => {
    if (mode === 'target') {
      const targetTag = d?.target ?? target ?? d?.tags?.[0]
      return targetTag ? [targetTag] : []
    }
    return d?.tags ?? []
  }, [d, mode, target])
  const methods = useMemo(() => Object.entries(d?.methods ?? {}), [d])
  const selectedMethods = useMemo(
    () => methods.filter(([key]) => selected[key] !== false),
    [methods, selected]
  )
  const methodColorByKey = useMemo(() => {
    const colors: Record<string, string> = {}
    methods.forEach(([key], index) => {
      colors[key] = methodColor(mode, index)
    })
    return colors
  }, [methods, mode])
  const tag =
    mode === 'target'
      ? tags[0]
      : selTag && tags.includes(selTag)
        ? selTag
        : tags[0]

  const toggleMethod = (key: string) => {
    setSelected((prev) => ({
      ...prev,
      [key]: !(prev[key] !== false),
    }))
  }

  const heatmaps = useMemo(
    () =>
      mode === 'multitag'
        ? methods.map(([key, meth]) => {
            const z = tags.map((t) => {
              const r = meth.residual[t] ?? []
              const s = meth.err_scale[t] || 1
              return r.map((v) => (v === null ? null : Math.abs(v) / s))
            })
            const data: Partial<PlotData>[] = [
              {
                type: 'heatmap',
                x: d?.ts ?? [],
                y: tags.map(short),
                z,
                zmin: 0,
                zmax: 8,
                // eslint-disable-next-line lingui/no-unlocalized-strings -- Plotly colorscale enum, not user-facing copy.
                colorscale: 'Inferno',
                // eslint-disable-next-line lingui/no-unlocalized-strings -- Plotly chart text is deferred in this extraction wave.
                colorbar: { title: { text: '|resid| / err' } },
              } as Partial<PlotData>,
            ]
            const layout: Partial<Layout> = {
              paper_bgcolor: 'rgba(0,0,0,0)',
              plot_bgcolor: 'rgba(0,0,0,0)',
              margin: { l: 150, r: 20, t: 10, b: 40 },
              xaxis: {
                ...dateAxis(
                  d?.window?.start ?? startDate,
                  d?.window?.end ?? endDate
                ),
                tickfont: { color: FG, size: 9 },
              },
              yaxis: { tickfont: { color: FG, size: 9 } },
            }
            return { key, data, layout }
          })
        : [],
    [methods, tags, d, mode, startDate, endDate]
  )

  const drill = useMemo(() => {
    if (!d || !tag) return null
    const actual: Partial<PlotData> = {
      x: d.ts,
      y: d.actual?.[tag] ?? [],
      type: 'scatter',
      mode: 'lines',
      name: 'actual',
      line: { width: 1.6, color: CantileverTheme.colors.grayscale['100'] },
    }
    const expected: Partial<PlotData>[] = selectedMethods.flatMap(
      ([key, meth]) => {
        const color = methodColorByKey[key]
        const label = methodLabels[key] ?? key
        const dates = d.ts ?? []
        const expectedVals = meth.expected[tag] ?? []
        const expectedTrace: Partial<PlotData> = {
          x: dates,
          y: expectedVals,
          type: 'scatter',
          mode: 'lines',
          name: `expected (${label})`,
          line: { width: 1.2, dash: 'dot', color },
        }
        if (mode !== 'target') return [expectedTrace]
        const band = meth.residual_band?.[tag]
        if (band === null || band === undefined || !Number.isFinite(band))
          return [expectedTrace]
        return [
          expectedTrace,
          ...expectedBandTraces(dates, expectedVals, band, color, label),
        ]
      }
    )
    const residual: Partial<PlotData>[] = selectedMethods.flatMap(
      ([key, meth]) => {
        const color = methodColorByKey[key]
        const label = methodLabels[key] ?? key
        const dates = d.ts ?? []
        const residualTrace: Partial<PlotData> = {
          x: dates,
          y: meth.residual[tag] ?? [],
          type: 'scatter',
          mode: 'lines',
          name: label,
          line: { width: 1.4, color },
        }
        if (mode !== 'target') return [residualTrace]
        const band = meth.residual_band?.[tag]
        if (band === null || band === undefined || !Number.isFinite(band))
          return [residualTrace]
        return [residualTrace, ...residualBandTraces(dates, band, color, label)]
      }
    )
    const scaledResidual: Partial<PlotData>[] =
      mode === 'target'
        ? selectedMethods.flatMap(([key, meth]) => {
            const color = methodColorByKey[key]
            const label = methodLabels[key] ?? key
            const dates = d.ts ?? []
            const scale = meth.err_scale[tag] || 1
            const residualTrace: Partial<PlotData> = {
              x: dates,
              y: (meth.residual[tag] ?? []).map((value) =>
                value === null ? null : value / Math.max(scale, 1e-12)
              ),
              type: 'scatter',
              mode: 'lines',
              name: label,
              line: { width: 1.4, color },
            }
            const bandK = meth.band_k
            if (!Number.isFinite(bandK) || dates.length === 0) {
              return [residualTrace]
            }
            return [
              residualTrace,
              ...residualBandTraces(dates, bandK, color, label),
            ]
          })
        : []
    const axis = (title: string): Partial<Layout> => ({
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      margin: { l: 60, r: 20, t: 10, b: 40 },
      // Prefer scored window; fall back to picker so the axis always matches the run.
      xaxis: dateAxis(d.window?.start ?? startDate, d.window?.end ?? endDate),
      yaxis: {
        title: { text: title, font: { color: FG, size: 11 } },
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
        zeroline: true,
        zerolinecolor: GRID,
      },
      legend: { orientation: 'h', font: { color: FG, size: 11 } },
    })
    return {
      actualExpected: {
        data: [actual, ...expected],
        layout: axis(short(tag)),
      },
      residual: {
        data: residual,
        layout: axis(`${short(tag)} actual − expected`),
      },
      scaledResidual: {
        data: scaledResidual,
        layout: axis(`${short(tag)} residual / error scale`),
      },
    }
  }, [
    d,
    tag,
    selectedMethods,
    methodColorByKey,
    methodLabels,
    mode,
    startDate,
    endDate,
  ])
  return (
    <div>
      <RunRow>
        <DateInput
          type='date'
          value={startDate}
          max={endDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <span>to</span>
        <DateInput
          type='date'
          value={endDate}
          min={startDate}
          max={isoDay()}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <Button
          variant='outline'
          disabled={state.status === 'running'}
          onClick={() => run(entryId, site, startDate, endDate)}>
          {state.status === 'running' ? t`Scoring…` : t`Run now`}
        </Button>
        <RangeHint>
          <Trans>default last 7 days · up to </Trans>
          {MAX_SCORE_DAYS}
          <Trans> days, native interval</Trans>
        </RangeHint>
      </RunRow>
      {state.status === 'error' && <ErrorText>{state.error}</ErrorText>}

      {d && (
        <>
          <Subtitle>
            <Trans>Chart x-axis pinned to </Trans>
            {d.window?.start?.slice(0, 10) ?? startDate} →{' '}
            {d.window?.end?.slice(0, 10) ?? endDate}
            <Trans>. Fresh data</Trans>{' '}
            {new Date(d.window?.start ?? '').toLocaleString()} —{' '}
            {new Date(d.window?.end ?? '').toLocaleString()}
            <Trans> (every</Trans> {d.window?.interval_minutes}
            <Trans> min), scored with models trained through </Trans>
            {d.trained_split_date}
            <Trans> (saved</Trans>{' '}
            {d.model_created_at
              ? new Date(d.model_created_at).toLocaleString()
              : 'unknown'}
            <Trans>). No retraining.</Trans>
          </Subtitle>

          {heatmaps.map((h) => (
            <React.Fragment key={h.key}>
              <SubTitle>
                <Trans>Residuals — </Trans>
                {methodLabels[h.key] ?? h.key}
              </SubTitle>
              <SizedPlot
                data={h.data}
                layout={h.layout}
                $heightRem={Math.max(17.5, 1.25 * tags.length + 5)}
              />
            </React.Fragment>
          ))}

          {drill && tag && (
            <>
              <SubTitle>
                {mode === 'target'
                  ? t`Target scoring — ${{ value451_42: short(tag) }} (native interval)`
                  : t`Tag drilldown (native interval)`}
              </SubTitle>
              {mode === 'multitag' && (
                <NativeSelect
                  value={tag}
                  onChange={(e) => setSelTag(e.target.value)}>
                  {tags.map((t) => (
                    <option key={t} value={t}>
                      {short(t)} ({famOf[t] ?? '?'})
                    </option>
                  ))}
                </NativeSelect>
              )}
              {mode === 'target' && methods.length > 0 && (
                <ModelFilterRow>
                  {methods.map(([key]) => {
                    const color = methodColorByKey[key]
                    const label = methodLabels[key] ?? key
                    return (
                      <ModelFilterLabel key={key}>
                        <Checkbox
                          checked={selected[key] !== false}
                          onCheckedChange={() => toggleMethod(key)}
                          aria-label={t`Show ${label}`}
                        />
                        <ColorDot $color={color} aria-hidden />
                        {label}
                      </ModelFilterLabel>
                    )
                  })}
                </ModelFilterRow>
              )}
              {mode === 'target' && (
                <Subtitle>
                  <Trans>
                    Click a point outside the band to open a case for that
                    breach — then run RCA from the Cases tab.
                  </Trans>
                  {caseState.status === 'running' && t` Opening case…`}
                  {caseState.status === 'done' &&
                    t` Case opened at ${{ value492_41: caseState.breachTs ?? '' }} — see the Monitor tab.`}
                </Subtitle>
              )}
              {mode === 'target' && caseState.status === 'error' && (
                <ErrorText>{caseState.error}</ErrorText>
              )}
              <SizedPlot
                data={drill.actualExpected.data}
                layout={drill.actualExpected.layout}
                $heightRem={17.5}
                onClick={mode === 'target' ? onPointClick : undefined}
              />
              <SizedPlot
                data={drill.residual.data}
                layout={drill.residual.layout}
                $heightRem={17.5}
              />
              {mode === 'target' && (
                <SizedPlot
                  data={drill.scaledResidual.data}
                  layout={drill.scaledResidual.layout}
                  $heightRem={17.5}
                  onClick={onPointClick}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
